import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Sparkle } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/Brand";

const STEPS = [
  { key: "temel", title: "Temel Bilgiler" },
  { key: "faaliyet", title: "Faaliyet & Ürün" },
  { key: "hedef", title: "Hedef Kitle" },
  { key: "marka_tonu", title: "Marka Tonu" },
  { key: "rekabet", title: "Rekabet & Notlar" },
];

const BRAND_TONE_OPTIONS = [
  { key: "professional", label: "Profesyonel",       desc: "Kurumsal, güven veren, ölçülü. 3-4 kısa paragraf, kısaltmasız." },
  { key: "friendly",     label: "Samimi",            desc: "Sıcak, günlük konuşma dili, kısa cümleler, satış baskısı yok." },
  { key: "premium",      label: "Prestijli",         desc: "Zarif, üst-segment, gösterişsiz. Prestij ima edilir, söylenmez." },
  { key: "direct",       label: "Direkt",            desc: "2-3 çok kısa cümle. İlk satırda değer önerisi, sonunda net istek." },
  { key: "consultative", label: "Danışmansal",       desc: "Soru-odaklı; önce içgörü, sonra öneri. Merak, baskı değil." },
];

export default function Onboarding() {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    isletme_adi: "", web_sitesi: "", instagram: "",
    faaliyet_alani: "", urun_hizmet: "",
    hedef_musteri: "", hizmet_sehirleri: "", hedef_ulkeler: "",
    brand_tone: "professional",
    rakipler: "", ek_aciklamalar: "",
  });
  const nav = useNavigate();
  const { refreshUser } = useAuth();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.isletme_adi || !form.faaliyet_alani || !form.urun_hizmet || !form.hedef_musteri) {
      toast.error("Lütfen zorunlu alanları doldurun");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        hizmet_sehirleri: form.hizmet_sehirleri.split(",").map((s) => s.trim()).filter(Boolean),
        hedef_ulkeler: form.hedef_ulkeler.split(",").map((s) => s.trim()).filter(Boolean),
      };
      await api.post("/business/profile", payload);
      await refreshUser();
      toast.success("İşletme profiliniz kaydedildi. Gerçek müşteri keşfine başlayabilirsiniz.");
      nav("/panel");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const s = STEPS[step];
  const canGoNext = () => {
    if (step === 0) return !!form.isletme_adi.trim();
    if (step === 1) return !!form.faaliyet_alani.trim() && !!form.urun_hizmet.trim();
    if (step === 2) return !!form.hedef_musteri.trim();
    if (step === 3) return !!form.brand_tone;
    return true;
  };

  return (
    <div className="min-h-screen bg-background acas-grid">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="mb-8">
          <Wordmark size="sm" showTagline taglineOverride="Kurulum" />
        </div>

        <div className="acas-eyebrow mb-2">Adım {step + 1} / {STEPS.length}</div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2">İşletmenizi tanıyalım</h1>
        <p className="text-muted-foreground mb-8 max-w-xl">
          Bu bilgileri AI ajanlarımız potansiyel müşterileri filtrelemek, puanlamak ve size özel içgörüler üretmek için kullanacak.
        </p>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-8">
          {STEPS.map((st, i) => (
            <React.Fragment key={st.key}>
              <div className={`flex items-center gap-2 text-xs ${i <= step ? "text-primary" : "text-muted-foreground"}`}>
                <div className={`w-6 h-6 rounded-full grid place-items-center border ${
                  i < step ? "bg-primary text-primary-foreground border-primary" :
                  i === step ? "border-primary text-primary" : "border-border"
                }`}>
                  {i < step ? <Check size={12} weight="bold" /> : i + 1}
                </div>
                <span className="hidden sm:inline">{st.title}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < step ? "bg-primary" : "bg-border"}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="rounded-lg border border-border/60 bg-card p-6 sm:p-8 space-y-5 acas-fade-up" key={step}>
          <h2 className="text-xl font-bold tracking-tight flex items-center gap-2">
            <Sparkle size={18} weight="fill" className="text-primary" /> {s.title}
          </h2>

          {step === 0 && (
            <>
              <Field label="İşletme Adı *"><Input value={form.isletme_adi} onChange={(e) => set("isletme_adi", e.target.value)} data-testid="ob-isletme" placeholder="Örn: El Emeği Butik" /></Field>
              <Field label="Web Sitesi"><Input value={form.web_sitesi} onChange={(e) => set("web_sitesi", e.target.value)} data-testid="ob-web" placeholder="https://www.markaniz.com" /></Field>
              <Field label="Instagram"><Input value={form.instagram} onChange={(e) => set("instagram", e.target.value)} data-testid="ob-instagram" placeholder="@markaniz" /></Field>
            </>
          )}
          {step === 1 && (
            <>
              <Field label="Faaliyet Alanı *"><Input value={form.faaliyet_alani} onChange={(e) => set("faaliyet_alani", e.target.value)} data-testid="ob-faaliyet" placeholder="El yapımı takı üretimi, endüstriyel yazılım, kozmetik vb." /></Field>
              <Field label="Ürün veya Hizmet *"><Textarea rows={4} value={form.urun_hizmet} onChange={(e) => set("urun_hizmet", e.target.value)} data-testid="ob-urun" placeholder="Sunduğunuz ürün ve hizmetleri detaylı açıklayın" /></Field>
            </>
          )}
          {step === 2 && (
            <>
              <Field label="Hedef Müşteri *"><Textarea rows={3} value={form.hedef_musteri} onChange={(e) => set("hedef_musteri", e.target.value)} data-testid="ob-hedef" placeholder="Sektör, ölçek, karar verici profili ve ihtiyaçları tanımlayın" /></Field>
              <Field label="Hizmet Verilen Şehirler" hint="Virgülle ayırın">
                <Input value={form.hizmet_sehirleri} onChange={(e) => set("hizmet_sehirleri", e.target.value)} data-testid="ob-sehirler" placeholder="İstanbul, Ankara, İzmir" />
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {form.hizmet_sehirleri.split(",").map((s) => s.trim()).filter(Boolean).map((c) => (
                    <Badge key={c} variant="secondary">{c}</Badge>
                  ))}
                </div>
              </Field>
              <Field label="Hedef Ülkeler" hint="Virgülle ayırın">
                <Input value={form.hedef_ulkeler} onChange={(e) => set("hedef_ulkeler", e.target.value)} data-testid="ob-ulkeler" placeholder="Türkiye, Almanya, İngiltere" />
              </Field>
            </>
          )}
          {step === 3 && (
            <>
              <div className="rounded-md border border-primary/25 bg-primary/[0.04] p-3 text-sm">
                <strong>Marka Sesiniz</strong>
                <div className="text-xs text-muted-foreground mt-1">
                  Bu ton; AI'nın hazırlayacağı ilk temas mesajlarında ve karar verici tespit
                  edilemediğinde oluşturulan yönlendirme notlarında kullanılır. Ayarlar
                  sayfasından istediğiniz zaman değiştirebilirsiniz.
                </div>
              </div>
              <div className="grid gap-3">
                {BRAND_TONE_OPTIONS.map(({ key, label, desc }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => set("brand_tone", key)}
                    data-testid={`ob-brand-tone-${key}`}
                    className={`text-left rounded-lg border p-4 transition ${
                      form.brand_tone === key
                        ? "border-primary bg-primary/[0.06] ring-2 ring-primary/30"
                        : "border-border/60 hover:border-primary/40"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold">{label}</div>
                        <div className="text-sm text-muted-foreground mt-1">{desc}</div>
                      </div>
                      <div className={`w-5 h-5 rounded-full border grid place-items-center flex-shrink-0 ${
                        form.brand_tone === key ? "border-primary bg-primary text-primary-foreground" : "border-border"
                      }`}>
                        {form.brand_tone === key && <Check size={12} weight="bold" />}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
          {step === 4 && (
            <>
              <Field label="Rakipler" hint="Bilinen rakiplerinizi listeleyin">
                <Textarea rows={3} value={form.rakipler} onChange={(e) => set("rakipler", e.target.value)} data-testid="ob-rakipler" placeholder="Rakip firma adları veya web siteleri" />
              </Field>
              <Field label="Ek Açıklamalar">
                <Textarea rows={3} value={form.ek_aciklamalar} onChange={(e) => set("ek_aciklamalar", e.target.value)} data-testid="ob-aciklamalar" placeholder="AI ajanlarımızın bilmesi gereken başka bir şey" />
              </Field>
            </>
          )}
        </div>

        <div className="flex justify-between mt-6">
          <Button variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="ob-back" className="gap-2">
            <ArrowLeft size={16} /> Geri
          </Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={() => setStep(step + 1)} disabled={!canGoNext()} data-testid="ob-next" className="gap-2">
              Devam <ArrowRight size={16} />
            </Button>
          ) : (
            <Button onClick={submit} disabled={busy} data-testid="ob-submit" className="gap-2">
              {busy ? "Kaydediliyor…" : "Kurulumu tamamla"} <Sparkle size={16} weight="fill" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div>
      <Label className="text-sm">{label}</Label>
      {hint && <div className="text-[11px] text-muted-foreground mb-1.5">{hint}</div>}
      <div className={hint ? "" : "mt-1.5"}>{children}</div>
    </div>
  );
}
